import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="px-6 py-6 lg:px-12 border-b border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="px-6 py-12 lg:px-12">
        <article className="max-w-4xl mx-auto prose-invert">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold uppercase mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground text-sm mb-10">Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">1. Who We Are</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              TOOLIO (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the tool-io.uk website and TOOLIO application. 
              TOOLIO is a tool identification and ownership inventory platform designed for contractors, tradespeople, and DIY enthusiasts. 
              For any questions about this privacy policy or your personal data, please contact us at{" "}
              <a href="mailto:hello@tool-io.uk" className="text-primary hover:underline">hello@tool-io.uk</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">2. What Data We Collect</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-3">We collect the following information when you use TOOLIO:</p>
            
            <h3 className="text-base font-bold mb-2 text-primary/80">Account Information</h3>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 mb-4 space-y-1">
              <li>Email address</li>
              <li>Password (stored securely using one-way encryption)</li>
              <li>Phone number (optional, used for contact in theft recovery)</li>
              <li>Postcode (optional, used for regional theft alerts)</li>
              <li>Profile image (optional)</li>
            </ul>

            <h3 className="text-base font-bold mb-2 text-primary/80">Tool Data</h3>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 mb-4 space-y-1">
              <li>Tool photographs and label images</li>
              <li>Manufacturer, model number, and serial number</li>
              <li>Tool type and description</li>
              <li>Estimated replacement value</li>
              <li>Tool status (Active, Stolen, Sold, Discarded, Written Off)</li>
              <li>Ownership and status change history</li>
            </ul>

            <h3 className="text-base font-bold mb-2 text-primary/80">Usage Data</h3>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li>IP address and approximate location when scanning or checking a tool</li>
              <li>Browser type and device information</li>
              <li>Session data for authentication</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">3. How We Use Your Data</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-3">We use your data for the following purposes:</p>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong>Tool Registration &amp; Management:</strong> To create and maintain your tool inventory, generate unique TOOLIO IDs, and track tool ownership.</li>
              <li><strong>AI-Powered Scanning:</strong> Tool images are processed using AI services to extract manufacturer, model, and serial number information. Images are not stored by the AI provider after processing.</li>
              <li><strong>Theft Protection:</strong> To flag stolen tools, notify you when a stolen tool is scanned, and share relevant information with law enforcement.</li>
              <li><strong>Insurance Reports:</strong> To generate professional PDF reports of your tool inventory and stolen items for insurance claim purposes.</li>
              <li><strong>Price Estimation:</strong> To search publicly available retail pricing for your tool model to estimate replacement values.</li>
              <li><strong>Communication:</strong> To send verification emails, password resets, and theft alert notifications.</li>
              <li><strong>Public Tool Check:</strong> To allow anyone to verify whether a tool has been reported as stolen. Only the tool&apos;s registration status is shown publicly — no owner details are revealed to the public.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">4. Who We Share Your Data With</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-3">We do not sell your personal data. We may share data with:</p>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong>Law Enforcement:</strong> When a tool is marked as stolen, authorised police personnel can access the tool owner&apos;s contact details (name, email, phone number) through the secure Police Portal to assist with recovery.</li>
              <li><strong>AI Service Providers:</strong> Tool images are sent to OpenAI for text recognition. Images are processed in real-time and not retained by the provider.</li>
              <li><strong>Email Service Provider:</strong> We use a third-party email service to send transactional emails (verification, password resets, notifications).</li>
              <li><strong>Image Hosting:</strong> Tool photographs are stored securely using a cloud image hosting service.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">5. Legal Basis for Processing</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">Under UK GDPR, we process your data on the following bases:</p>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 mt-3 space-y-1">
              <li><strong>Contract:</strong> Processing necessary to provide the TOOLIO service you signed up for.</li>
              <li><strong>Legitimate Interest:</strong> Theft prevention, fraud detection, and platform security.</li>
              <li><strong>Legal Obligation:</strong> Cooperating with law enforcement regarding stolen property.</li>
              <li><strong>Consent:</strong> Optional data such as phone number and postcode, which you can withdraw at any time.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">6. How Long We Keep Your Data</h2>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong>Active accounts:</strong> Your data is retained for as long as your account is active.</li>
              <li><strong>Deleted accounts:</strong> Upon account deletion, your personal data is removed within 30 days. Tool records that have been marked as stolen may be retained in an anonymised form to support ongoing theft investigations.</li>
              <li><strong>Audit logs:</strong> Security and access logs are retained for up to 12 months.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">7. Your Rights</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-3">Under UK data protection law, you have the right to:</p>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification:</strong> Ask us to correct inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data.</li>
              <li><strong>Data Portability:</strong> Export your tool inventory data (available via CSV and PDF export within the app).</li>
              <li><strong>Withdraw Consent:</strong> Remove optional information (phone, postcode) from your profile at any time.</li>
              <li><strong>Object:</strong> Object to processing based on legitimate interest.</li>
            </ul>
            <p className="text-muted-foreground text-sm leading-relaxed mt-3">
              To exercise any of these rights, or to request deletion of your account and all associated data, please email{" "}
              <a href="mailto:hello@tool-io.uk" className="text-primary hover:underline">hello@tool-io.uk</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">8. Cookies &amp; Sessions</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              TOOLIO uses essential session cookies to keep you logged in. We do not use advertising or tracking cookies. 
              No third-party analytics or tracking scripts are loaded on the platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">9. Data Security</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We take the security of your data seriously. Measures include:
            </p>
            <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 mt-3 space-y-1">
              <li>Passwords are hashed using industry-standard one-way encryption</li>
              <li>All data is transmitted over HTTPS</li>
              <li>Rate limiting and brute-force protection on all authentication endpoints</li>
              <li>Input sanitisation to prevent injection attacks</li>
              <li>Regular automated backups of all data</li>
              <li>Security practices aligned with OWASP Top 10 and Cyber Essentials guidelines</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">10. Children&apos;s Privacy</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              TOOLIO is not intended for use by anyone under the age of 16. We do not knowingly collect personal data from children. 
              If you believe a child has provided us with personal data, please contact us at{" "}
              <a href="mailto:hello@tool-io.uk" className="text-primary hover:underline">hello@tool-io.uk</a> and we will delete it promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">11. Changes to This Policy</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We may update this privacy policy from time to time. Any changes will be posted on this page with an updated &quot;Last updated&quot; date. 
              We encourage you to review this page periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold mb-3">12. Contact Us</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              If you have any questions about this privacy policy, your personal data, or wish to make a complaint, please contact us:
            </p>
            <div className="mt-3 p-4 border border-border rounded-lg bg-card">
              <p className="text-sm font-bold mb-1">TOOLIO</p>
              <p className="text-sm text-muted-foreground">
                Email: <a href="mailto:hello@tool-io.uk" className="text-primary hover:underline">hello@tool-io.uk</a>
              </p>
              <p className="text-sm text-muted-foreground">
                Website: <a href="https://tool-io.uk" className="text-primary hover:underline">tool-io.uk</a>
              </p>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed mt-3">
              You also have the right to lodge a complaint with the Information Commissioner&apos;s Office (ICO) at{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ico.org.uk</a> if you believe your data has been handled improperly.
            </p>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 lg:px-12 border-t border-border">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          </Link>
          <p className="text-muted-foreground text-sm">
            &copy; {new Date().getFullYear()} TOOLIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
